import React, { useState, useEffect, useContext, useMemo } from 'react'
import useApi from 'hooks/useApi'
import messages from './messages'
import VersionPage from './components/VersionPage'
import OverviewPage from './components/OverviewPage'
import JobPage from './components/JobPage'
import TaskPage from './components/TaskPage'
import { FormattedMessage, FormattedTime, FormattedDate, useIntl } from 'react-intl'
import { useKeyboardShortcut, useDebounce } from '@arnsbogroup/react-voicearchive/lib/hooks'
import DateTime from 'react-datetime'
import { NavLink, Link, Switch, Route, useParams } from 'react-router-dom'
import TreeView, { flattenTree } from 'react-accessible-treeview'
import { Icon, Button, MenuButton, Chip, Label } from '@arnsbogroup/react-voicearchive'
import { Menu, Transition } from '@headlessui/react'
import classNames from 'classnames'
import NiceModal from '@ebay/nice-modal-react'
import OrderFlagFragments from 'components/OrderFlagFragments'

import ReportIncidentModal from 'components/ReportIncidentModal'
import BulkMessageSuppliersModal from './components/BulkMessageSuppliersModal'
import BulkCreateJobsModal from './components/BulkCreateJobsModal'
import BulkCreateVersionsModal from './components/BulkCreateVersionsModal'
import CreateTaskDropdown from './components/CreateTaskDropdown'

import ShowCustomerNoteDialog from "./components/TaskPage/components/ShowCustomerNoteDialog"

import useCurrentUser from 'hooks/useCurrentUser'

const tree = (branch) => ({
  ...branch,
  children: (branch.tasks || branch.versions || []).map(tree),
  name: branch,
  // Weird API, but basically we are storing the branch in "name"
  // to make all props available in the nodeRederer
})

const OrderPage = () => {
  const currentUser = useCurrentUser()
  const { orderId, jobId, versionId, taskId } = useParams()
  const { formatMessage } = useIntl()
  const [selectable, setSelectable] = useState(false)
  const [saving, setSaving] = useState(false)
  const [order, setOrder] = useState(null)
  const debouncedOrder = useDebounce(order, 500)
  const [selectedIds, setSelectedIds] = useState([])
  const [orderData, loading, errors, refetch, meta] = useApi(`/rest/v1/order/${orderId}?includeRelations=EXPERIMENTAL&includeTaskTree=1`)
  const data = useMemo(() => flattenTree(tree(order || {})), [order])
  const selectedTasks = useMemo(() => {
    return data
      .filter(({ id }) => selectedIds.includes(id))
      .map(({ name: task }) => task)
  }, [data, selectedIds])

  useEffect(() => setOrder(orderData), [orderData])
  useEffect(() => {
    setSelectedIds([])
    fetch(`/rest/v1/actionLogEntry`, {
      method: 'POST',
      body: JSON.stringify({
        message: "User opened order",
        orderId: orderId,
        username: currentUser.username,
        userId: currentUser.id,
        strategy: "user-opened-order"
      })
    })
      .then((response) => response.json())
      .catch((error) => toastr.error(error))
  }, [orderId])


  const onChange = (nextState) => {
    setOrder(nextState)
  }

  const readOnly = order?.readOnly || loading || saving

  useEffect(() => {
    if (!debouncedOrder) {
      return
    }

    if (!Object.entries(debouncedOrder).some(([key, value]) => orderData[key] !== value)) {
      return
    }

    setSaving(true)

    const body = {}

    Object.entries(debouncedOrder).map(([key, value]) => {
      if (orderData[key] !== value) {
        if (['mediaTypes', 'audioFormatTypes', 'services', 'products'].includes(key)) {
          body[{
            mediaTypes: 'medias',
            audioFormatTypes: 'audioformats',
            services: 'services',
            products: 'products',
          }[key]] = value?.map(({ uid }) => uid) || []
        } else {
          body[key] = value
        }
      }
    })

    fetch(`/rest/v1/order/${orderId}`, { method: 'PUT', body: JSON.stringify(body) })
      .then(() => setSaving(false))
      .then(refetch)
      .catch((error) => window.alert(error))
  }, [debouncedOrder])

  const resolveTo = (element) => {
    for (const job of order.tasks) {
      if (parseInt(job.uid) == parseInt(element.uid)) {
        return `/orders/${order.uid}/jobs/${job.uid}`
      }

      for (const version of job.versions) {
        if (parseInt(version.uid) == parseInt(element.uid)) {
          return `/orders/${order.uid}/jobs/${job.uid}/versions/${version.uid}`
        }

        for (const task of version.tasks) {
          if (parseInt(task.uid) == parseInt(element.uid)) {
            return `/orders/${order.uid}/jobs/${job.uid}/versions/${version.uid}/tasks/${task.uid}`
          }
        }
      }
    }

    return '/'
  }
  
  const resolveIncidentTask = () => {
    const foundTask = selectedTasks.find(({ uid }) => uid == taskId)
    if (foundTask) {
      return foundTask
    } else if (!!selectedTasks.length) {
      return selectedTasks[0]
    }
    return null
  }

  if (!order) {
    return null
  }

  const isEmptySkeleton = order?.tasks.length === 1
    && order?.tasks[0].versions.length === 0

  return (
    <div className="h-full w-full flex overflow-auto">
      <div className="grow h-full overflow-auto">
        <Switch>
          <Route
            path="/orders/:orderId/jobs/:jobId/versions/:versionId/tasks/:taskId"
            render={(props) => (
              <TaskPage
                {...props}
                onChange={onChange}
                order={order}
                refetch={refetch}
                versionId={versionId}
                jobId={jobId}
                version={order.tasks
                  .find(({ uid }) => uid == jobId).versions
                  .find(({ uid }) => uid == versionId)
                }
                task={order.tasks
                  .find(({ uid }) => uid == jobId).versions
                  .find(({ uid }) => uid == versionId).tasks
                  .find(({ uid }) => uid == taskId)
                }
              />
            )}
          />

          <Route
            path="/orders/:orderId/jobs/:jobId/versions/:versionId"
            render={(props) => (
              <VersionPage
                {...props}
                onChange={onChange}
                order={order}
                refetch={refetch}
                readOnly={readOnly}
                version={order.tasks
                  .find(({ uid }) => uid == jobId).versions
                  .find(({ uid }) => uid == versionId)
                }
              />
            )}
          />

          <Route
            path="/orders/:orderId/jobs/:jobId"
            render={(props) => (
              <JobPage
                {...props}
                onChange={onChange}
                job={order.tasks.find(({ uid }) => uid == jobId)}
                order={order}
                refetch={refetch}
                readOnly={readOnly}
              />
            )}
          />

          <Route
            path="/orders/:orderId"
            render={(props) => (
              <OverviewPage
                {...props}
                onChange={onChange}
                order={order}
                readOnly={readOnly}
                loading={loading || saving}
                refetch={refetch}
              />
            )}
          />
        </Switch>
      </div>

      <div className="w-1/3 xl:w-80 2xl:w-96 shrink-0 flex flex-col h-full bg-gray-50 divide-y divide-gray-200 shadow-inner">
        <div className="shrink-0 p-4">
          <Link
            className="hover:text-primary-darker hover:underline text-xs text-gray-500"
            to={`/customers/${order.customer.uid}`}
            title="Customer"
          >
            { order.customer.name }
          </Link>

          { saving ? (
            <h2 className="font-medium">Saving..</h2>
          ) : loading ? (
            <h2 className="font-medium">Loading..</h2>
          ) : (
            <>
              <h2 className="font-medium">
                <Link
                  className="hover:text-primary-darker hover:underline"
                  to={`/orders/${order.uid}`}
                >
                  { order.name }
                </Link>
              </h2>
              <div className="ml-1 space-x-1">
                <OrderFlagFragments order={order} />
                { order?.priority &&
                  <span title={formatMessage(messages.priority)}>
                    {{
                      LOW: <Chip>Low</Chip>,
                      MEDIUM: <Chip state="warning">MEDIUM</Chip>,
                      HIGH: <Chip state="error">HIGH</Chip>
                    } [order?.priority]}
                  </span>
                }
              </div>
            </>
          ) }

          <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:gap-2">
            <div className="flex items-center text-sm text-gray-500">
              <Icon icon={['fas', 'hashtag']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />
              <Link
                className="hover:text-primary-darker hover:underline"
                to={`/orders/${order.uid}`}
              >
                { order.uid }
              </Link>
            </div>

            { order?.deal_id?.length &&
              <div className="flex items-center text-sm text-gray-500">
                <Icon icon={['fas', 'link']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />
                <a
                  href={`https://my.voicearchive.com/crm/deal/details/${order.deal_id}/`}
                  target="_blank"
                  className="hover:text-primary-darker hover:underline"
                >
                  D{ order.deal_id }
                </a>
              </div>
            }
          </div>
            <div className="flex items-center text-sm text-gray-500">
              <Icon icon={['fas', 'calendar']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />
                <DateTime
                  value={new Date(+order.deadline * 1000)}
                  inputProps={{
                    disabled: readOnly && !order?.isDraft,
                    className: classNames('bg-transparent', {
                      'border-b border-dashed border-gray-300 cursor-pointer hover:border-gray-500': !readOnly || order?.isDraft
                    })
                  }}
                  onChange={(value) => onChange({ ...order, deadline: Math.round(new Date(value).getTime() / 1000) })}
                  closeOnTab
                />
            </div>
        </div>


        <div className="grow overflow-auto px-1">
        { order?.tasks.length === 0 &&
            <div className="p-4">
              <div className="border border-dashed border-gray-200 py-4 px-8 text-sm text-gray-400 text-center">
                <Button
                  disabled={readOnly}
                  variant="inline"
                  onClick={() => NiceModal.show(BulkCreateJobsModal, { order }).then(refetch)}
                >
                  + Create job
                </Button>
                <span>, to get started with your order.</span>
              </div>
            </div>
          }
          <TreeView
            data={data}
            aria-label="Checkbox tree"
            multiSelect
            propagateSelect
            propagateSelectUpwards
            defaultExpandedIds={[...Array(data.length).keys()]}
            selectedIds={selectedIds}
            onSelect={({ treeState }) => setSelectedIds([...treeState.selectedIds])}
            togglableSelect
            nodeRenderer={({
              element: {
                name: task,
                id,
              },
              isBranch,
              isExpanded,
              isSelected,
              isDisabled,
              isHalfSelected,
              getNodeProps,
              level,
              handleSelect,
              handleExpand,
              dispatch,
            }) => {
              const { onClick, ...rest } = getNodeProps({ onClick: handleExpand })

              return (
                <div
                  {...rest}
                  style={{
                    marginLeft: 16 * (level - 1),
                    opacity: isDisabled ? 0.5 : 1,
                  }}
                >
                  <div className="flex items justify-between">
                    <div className="flex items-center space-x-2 text-sm text-grayscale p-1 group">
                      { isBranch &&
                        <Icon onClick={onClick} icon={['fal', isExpanded ? 'caret-down' : 'caret-right']} />
                      }

                      { 'versions' in task && !task.versions.length &&
                        <Icon className="text-gray-300" icon={['fal', 'caret-right']} />
                      }

                      { selectable &&
                        <Icon
                          onClick={(e) => {
                            handleSelect(e);
                            e.stopPropagation();
                          }}
                          icon={isHalfSelected ? "minus-square" : isSelected ? "check-square" : "square" }
                        />
                      }

                      { task?.status &&
                        <i
                          className={classNames(
                            'shrink-0',
                            'block',
                            'w-2',
                            'h-2',
                            'rounded-full',
                            `${{
                              green: 'bg-positive',
                              red: 'bg-negative',
                              yellow: 'bg-warning',
                              gray: 'bg-gray-200',
                            }[task.status.color || 'bg-gray']}`
                          )}
                          title={task.status.name || ''}
                        />
                      }

                      <NavLink
                        to={resolveTo(task)}
                        activeClassName="text-primary font-medium underline"
                        className="text-gray-700 hover:text-primary-darker hover:font-medium flex items-center space-x-2"
                        onClick={(event) => event.stopPropagation()}
                      >
                        { task?.type?.name ? (
                          <>
                            <>{task.type.shortname}</> { !!task.supplier?.userid && <> ({ task.supplier?.userid }) </> }
                          </>
                        ) : (
                          <>{task.title || task.name || <i className="italic">Untitled</i>}</>
                        ) }


                        { task?.retakeReason &&
                          <Icon icon={['fas', 'exclamation']} className="text-negative ml-1" title="Retake" />
                        }

                        { 'versions' in task && !task.versions.length &&
                          <Chip size="xs" className="ml-1">Empty</Chip>
                        }
                      </NavLink>


                      { !+task.type?.uid && !+task.parenttask &&
                        <Button
                          disabled={readOnly}
                          variant="inline"
                          className="opacity-0 group-hover:opacity-100"
                          onClick={() => NiceModal.show(BulkCreateVersionsModal, { order, job: task } ).then(refetch)}
                        >
                          + Version
                        </Button>
                      }

                      { !+task.type?.uid && !!+task.parenttask && !readOnly &&
                        <CreateTaskDropdown refetch={refetch} order={order} version={task} />
                      }
                    </div>

                    { id === 1 &&
                      <Button
                        disabled={readOnly}
                        onClick={() => NiceModal.show(BulkCreateJobsModal, { order }).then(refetch)}
                        size="xs"
                        className="m-2"
                      >
                        + Job
                      </Button>
                    }
                  </div>

                  { isEmptySkeleton && !+task.type?.uid && !+task.parenttask &&
                    <div className="p-4">
                      <div className="border border-dashed border-gray-200 py-4 px-8 text-sm text-gray-400 text-center">
                        <Button
                          disabled={readOnly}
                          variant="inline"
                          onClick={() => NiceModal.show(BulkCreateVersionsModal, { order, job: task } ).then(refetch)}
                        >
                          + Create version
                        </Button>
                        <span>, to get started with your order.</span>
                      </div>
                    </div>
                  }
                </div>
              )
            }
            }
          />
        </div>

        <Menu as="div" className="shrink-0 relative px-4 py-2">
          <div className={classNames('w-full flex items-center', { 'justify-between': selectable, 'justify-end': !selectable })}>
            { selectable ? (
              <Button disabled={readOnly} variant="outline" size="xs" onClick={() => setSelectable(false)}>
                Cancel
              </Button>
            ) : (
              <Button disabled={readOnly} size="xs" onClick={() => setSelectable(true)}>
                Bulk select tasks
              </Button>
            ) }

            { selectable &&
              <Menu.Button disabled={!selectable || !selectedTasks.length}>
                <Button
                  variant={selectedTasks.length ? 'solid' : 'text'}
                  disabled={!selectedTasks.length}
                  size="xs"
                >
                  <span>Actions</span>

                  { !!selectedTasks.length &&
                    <span> ({ selectedTasks.length })</span>
                  }
                </Button>
              </Menu.Button>
            }
          </div>

          <Transition
            as={React.Fragment}
            enter="transition ease-out duration-200"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="-top-2 transform -translate-y-full absolute right-0 w-80 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <Menu.Item>
                {(props) => (
                  <MenuButton {...props} onClick={() => window.open('https://my.voicearchive.com/workgroups/group/326/tasks/task/view/66548/', '_blank')}>
                    <div className="flex justify-between w-full">
                      <span>Add production tasks</span>
                      <Chip state="warning">Depreacted</Chip>
                    </div>
                  </MenuButton>
                )}
              </Menu.Item>

              <Menu.Item>
                {(props) => (
                  <MenuButton {...props} onClick={() => window.open('https://my.voicearchive.com/workgroups/group/326/tasks/task/view/66548/', '_blank')}>
                    <div className="flex justify-between w-full">
                      <span>Bulk assign supplier</span>
                      <Chip state="warning">Depreacted</Chip>
                    </div>
                  </MenuButton>
                )}
              </Menu.Item>

              <Menu.Item>
                {(props) => (
                  <MenuButton {...props} disabled>
                    Hold
                  </MenuButton>
                )}
              </Menu.Item>
              <Menu.Item>
                {(props) => (
                  <MenuButton {...props} disabled={readOnly}>
                    Resume
                  </MenuButton>
                )}
              </Menu.Item>
              <Menu.Item>
                {(props) => (
                  <MenuButton
                    {...props}
                    disabled={readOnly}
                    onClick={() => {
                      NiceModal.show(ReportIncidentModal, {
                        task: resolveIncidentTask(),
                        selectedTaskIds: selectedTasks.map(({ uid }) => uid),
                      }).then(refetch)
                    }}
                  >
                    Report incident
                  </MenuButton>
                )}
              </Menu.Item>
              <Menu.Item>
                {(props) => (
                  <MenuButton
                    {...props}
                    disabled={readOnly}
                    onClick={() => {
                      NiceModal.show(BulkMessageSuppliersModal, {
                        order,
                        selectedTasks,
                      }).then(refetch)
                    }}
                  >
                    Message suppliers
                  </MenuButton>
                )}
              </Menu.Item>
              <Menu.Item>
                {(props) => (
                  <MenuButton
                    {...props}
                    disabled={readOnly}
                    onClick={() => {
                      NiceModal.show(ShowCustomerNoteDialog, {
                        tasks: selectedTasks.filter((task) => task?.status?.action == "SHOW_CUSTOMER_NOTE_DIALOG")
                      }).then(refetch)
                    }}
                  >
                    Make files Visible (May Notify Customer)
                  </MenuButton>
                )}
              </Menu.Item>

            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </div>
  )
}

export default OrderPage
